import { USER_TYPES } from "../helpers/enums"
import {
  renderAdminMenu,
  renderFacilityAdminMenu,
  renderFacilityUserMenu,
} from "../helpers/headerMenu"

// Legacy JSX render — kept for backward compat; Header.jsx now uses getNavItems()
export const renderMenu = (rolePermission, activeMenu) => {
  if (rolePermission.role === USER_TYPES.SUPER_ADMIN) {
    return renderAdminMenu(activeMenu)
  } else if (rolePermission.role === USER_TYPES.FACILITY_ADMIN) {
    return renderFacilityAdminMenu(rolePermission, activeMenu)
  } else if (rolePermission.role === USER_TYPES.FACILITY_USER) {
    return renderFacilityUserMenu(rolePermission, activeMenu)
  }
}

export const getRolePermissions = (permissionsMapping) => {
  const userAccess = permissionsMapping.userAccess ?? {}
  const permissions = {
    schedule_management: userAccess.schedule_management || null,
    candidates: userAccess.candidates || null,
    landing_page: userAccess.landing_page || null,
    scheduled_report: userAccess.scheduled_report || null,
    scheduling: userAccess.scheduling || null,
    spend_management_dashboard: userAccess.spend_management_dashboard || null,
    spend_management_menu: userAccess.spend_management_menu || null,
    tableau_subscription: permissionsMapping.tableau_subscription || null,
    locums_analytics: userAccess.locums_analytics || null,
  }

  const userInfo = {
    client_account_no: permissionsMapping?.client_account_no || null,
    parentAccountNo: permissionsMapping?.parentAccountNo || null,
    client_account_name: permissionsMapping?.client_account_name || null,
    first_name: permissionsMapping?.first_name || null,
    full_name: permissionsMapping?.full_name || null,
    is_active: permissionsMapping?.is_active || null,
    last_name: permissionsMapping?.last_name || null,
    user_id: permissionsMapping?.user_id || null,
    email: permissionsMapping?.email || null,
    account_name: permissionsMapping?.account_name || null,
    title: permissionsMapping?.title || null,
    phone: permissionsMapping?.phone || null,
  }
  // facilities/specialties are no longer read from the token (docs/jwt-slim-claims-plan.md);
  // roleReducer fills them from /api/currentUser and clears scopePending.
  const facilities = []
  const specialties = []
  const personas = permissionsMapping?.personas || []

  // ✅ Normalize persona values to lowercase for consistent comparison
  const normalizedPersonas = new Set(
    personas.map((p) => p.toLowerCase().trim()),
  )

  // ✅ Define flags for role detection
  // user_facility_admin (Admin Portal role-separation persona) gets the same
  // Client Portal VIEW/permissions as Super Admin -- Client Portal never
  // learned about this persona on its own, so without this it silently fell
  // through to the Facility User default. isActualSuperAdmin is kept
  // separate from hasSuperAdmin so the displayed role label (below) can
  // still say "User Facility Admin" instead of "Super Admin" even though
  // functionally they're granted the identical role.
  const isActualSuperAdmin =
    normalizedPersonas.has("super admin") ||
    normalizedPersonas.has("super_admin")
  const isUserFacilityAdmin =
    normalizedPersonas.has("user_facility_admin") ||
    normalizedPersonas.has("user facility admin") ||
    normalizedPersonas.has("user-facility-admin")
  const isReadOnlyAdmin =
    normalizedPersonas.has("ro_admin") ||
    normalizedPersonas.has("ro admin") ||
    normalizedPersonas.has("ro-admin")
  const hasSuperAdmin =
    isActualSuperAdmin || isUserFacilityAdmin || isReadOnlyAdmin
  const hasAdmin =
    normalizedPersonas.has("admin") || normalizedPersonas.has("administrator")

  // ✅ Apply final business logic
  let role // default fallback

  if (hasSuperAdmin && hasAdmin) {
    // Case 1: Both Super Admin & Admin
    role = USER_TYPES.SUPER_ADMIN
  } else if (hasSuperAdmin && !hasAdmin) {
    // Case 2: Only Super Admin (no Admin)
    role = USER_TYPES.SUPER_ADMIN
  } else if (!hasSuperAdmin && hasAdmin) {
    // Case 3: Only Admin
    role = USER_TYPES.FACILITY_ADMIN
  } else {
    // Case 4: Neither Super Admin nor Admin
    role = USER_TYPES.FACILITY_USER
  }

  // Display-only label -- what the UI shows (e.g. the home page role badge).
  // Distinct from `role` above so user_facility_admin reads as its own role
  // in the UI while still functioning identically to Super Admin everywhere
  // access is gated on `role`.
  // Display-only: read-only checked first so it wins over a stale super_admin.
  let roleLabel
  if (isReadOnlyAdmin) {
    roleLabel = USER_TYPES.READ_ONLY_ADMIN
  } else if (isActualSuperAdmin) {
    roleLabel = USER_TYPES.SUPER_ADMIN
  } else if (isUserFacilityAdmin) {
    roleLabel = USER_TYPES.USER_FACILITY_ADMIN
  } else {
    roleLabel = role
  }
  // Capability, not a role: every surface that downloads/exports/prints
  // checks this instead of comparing role strings.
  const canExport = !isReadOnlyAdmin
  // Extract mapping_permissions from token data
  const mappingPermissions = permissionsMapping?.mapping_permissions || {}
  const accessAllWorksite = mappingPermissions?.access_all_worksite === true
  const accessAllSpecialty = mappingPermissions?.access_all_specialty === true

  // ✅ Return based on role presence
  if (role) {
    return {
      role,
      roleLabel,
      permissions,
      userInfo,
      facilities,
      specialties,
      accessAllWorksite,
      accessAllSpecialty,
      canExport,
      scopePending: true,
    }
  } else {
    return {
      role: null,
      roleLabel: null,
      permissions: {},
      userInfo: {},
      facilities: [],
      specialties: [],
      accessAllWorksite: false,
      accessAllSpecialty: false,
      canExport: false,
      scopePending: true,
    }
  }
}

/**
 * True while a facility-scoped user's facilities/specialties have not yet
 * arrived from /api/currentUser. Consumers should show a loading state, not
 * decide access, while this is true.
 */
export const isFacilityScopePending = (rolePermission = {}) =>
  rolePermission.role !== USER_TYPES.SUPER_ADMIN &&
  rolePermission.scopePending === true &&
  !(
    rolePermission.accessAllWorksite === true &&
    rolePermission.accessAllSpecialty === true
  )
